import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as actions from '../actions';
import { OutletService, AuthorService } from 'app/services';
import { tap, mergeMap, map, catchError, switchMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { find } from 'lodash';

@Injectable()
export class AuthorsOutletsListEffects {
  constructor(
    private actions$: Actions,
    private authorService: AuthorService,
    private outletService: OutletService
  ) {}

  loadAuthorsSaved$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadAuthorsOutlets),
      mergeMap((action) =>
        this.authorService.getAuthorsSaved(action.projectId).pipe(
          switchMap((response) => [
            actions.loadAuthorsSuccess({
              authors: response,
            }),
          ]),
          catchError((err) =>
            of(actions.loadAuthorsOutletsError({ payload: err }))
          )
        )
      )
    )
  );

  loadOutletsSaved$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadAuthorsOutlets),
      mergeMap((action) =>
        this.outletService.getOutletsSaved(action.projectId).pipe(
          switchMap((response) => [
            actions.loadOutletsSuccess({
              outlets: response,
            }),
          ]),
          catchError((err) =>
            of(actions.loadAuthorsOutletsError({ payload: err }))
          )
        )
      )
    )
  );

  addAuthorToProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.addAuthorToProject),
      mergeMap((action) =>
        this.addPartyWithLabel(
          action.partyId,
          action.userProject.value,
          action.userLabelId,
          true
        ).pipe(
          switchMap((response) => [
            actions.addAuthorToProjectSuccess({
              author: action.author,
            }),
            actions.loadAuthorsOutlets({
              projectId: action.userProject.value,
            }),
          ]),
          catchError((err) =>
            of(
              actions.addAuthorOutletToProjectError({
                payload: err,
              })
            )
          )
        )
      )
    )
  );

  addOutletToProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.addOutletToProject),
      mergeMap((action) =>
        this.addPartyWithLabel(
          action.partyId,
          action.userProject.value,
          action.userLabelId,
          false
        ).pipe(
          switchMap((response) => [
            actions.addOutletToProjectSuccess({
              outlet: action.outlet,
            }),
            actions.loadAuthorsOutlets({
              projectId: action.userProject.value,
            }),
          ]),
          catchError((err) =>
            of(
              actions.addAuthorOutletToProjectError({
                payload: err,
              })
            )
          )
        )
      )
    )
  );

  deleteAuthorToProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.deleteAuthorToProject),
      mergeMap((action) =>
        this.authorService
          .deleteAuthorOutletToProject(action.userProject.value, action.partyId)
          .pipe(
            tap(),
            switchMap((response) => [
              actions.deleteAuthorToProjectSuccess({
                author: action.author,
              }),
              actions.loadAuthorsOutlets({
                projectId: action.userProject.value,
              }),
            ]),
            catchError((err) =>
              of(
                actions.deleteAuthorOutletToProjectError({
                  payload: err,
                })
              )
            )
          )
      )
    )
  );

  deleteOutletToProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.deleteOutletToProject),
      mergeMap((action) =>
        this.authorService
          .deleteAuthorOutletToProject(action.userProject.value, action.partyId)
          .pipe(
            tap(),
            switchMap((response) => [
              actions.deleteOutletToProjectSuccess({
                outlet: action.outlet,
              }),
              actions.loadAuthorsOutlets({
                projectId: action.userProject.value,
              }),
            ]),
            catchError((err) =>
              of(
                actions.deleteAuthorOutletToProjectError({
                  payload: err,
                })
              )
            )
          )
      )
    )
  );

  updateAuthorLabel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.updateAuthorLabel),
      mergeMap((action) =>
        this.authorService
          .updateAuthorOutletToProject(action.userPartyId, action.userLabelId)
          .pipe(
            switchMap(() => [
              actions.addAuthorToProjectSuccess({ author: action.author }),
              actions.loadAuthorsOutlets({
                projectId: action.userProject.value,
              }),
            ]),
            catchError((err) =>
              of(actions.addAuthorOutletToProjectError({ payload: err }))
            )
          )
      )
    )
  );

  updateOutletLabel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.updateOutletLabel),
      mergeMap((action) =>
        this.outletService
          .updateAuthorOutletToProject(action.userPartyId, action.userLabelId)
          .pipe(
            switchMap(() => [
              actions.addOutletToProjectSuccess({ outlet: action.outlet }),
              actions.loadAuthorsOutlets({
                projectId: action.userProject.value,
              }),
            ]),
            catchError((err) =>
              of(actions.addAuthorOutletToProjectError({ payload: err }))
            )
          )
      )
    )
  );

  private addPartyWithLabel(
    partyId: number,
    projectId: number,
    userLabelId: number,
    isAuthor: boolean
  ): Observable<any> {
    return this.authorService.addAuthorOutletToProject(partyId, projectId).pipe(
      switchMap((response) => {
        if (userLabelId === null || userLabelId === undefined) {
          return of(response);
        }
        const responseId = response?.id || response?.userPartyId;
        if (responseId) {
          return this.authorService.updateAuthorOutletToProject(
            responseId,
            userLabelId
          );
        }
        const savedParties$ = isAuthor
          ? this.authorService.getAuthorsSaved(projectId)
          : this.outletService.getOutletsSaved(projectId);
        return savedParties$.pipe(
          switchMap((savedParties) => {
            const savedParty = find(
              savedParties,
              (item) => item.partyId === partyId
            );
            if (!savedParty?.id) {
              throw new Error('Saved author or outlet record was not returned');
            }
            return this.authorService.updateAuthorOutletToProject(
              savedParty.id,
              userLabelId
            );
          })
        );
      })
    );
  }
}
